import { useFormik } from "formik";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DateRangePicker,
  Paper,
  Typography,
  MultipleAutocomplete,
  MultipleAutocompleteOption,
  Input,
} from "../../components";
import { useAxios } from "../../hooks";
import { formatMoney } from "../../utils";
import { useTheme } from "@mui/material";
import currency from "currency.js";

function getDatesBetween(startDate: Date, endDate: Date) {
  const dates = [];
  let currentDate = moment(startDate);
  const finalDate = moment(endDate);

  while (currentDate.isSameOrBefore(finalDate)) {
    dates.push(currentDate.format('DD/MM/YYYY'));
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
}

function getMonthsBetween(startDate: Date, endDate: Date) {
  const months = [];
  let current = moment(startDate).startOf('month');
  const final = moment(endDate).startOf('month');

  while (current.isSameOrBefore(final)) {
    months.push(current.format('MM/YYYY'));
    current.add(1, 'month');
  }

  return months;
}


export const OrderProfit = () => {
  const theme = useTheme()
  const [dashboard, refetchDashboard] = useAxios("/dashboard/all-order-profits", {
    manual: true,
  });

  const [vision, setVision] = useState<'daily' | 'monthly'>('monthly')

  const searchFormik = useFormik({
    initialValues: {
      from: moment().subtract(12, "month").toDate(),
      to: moment().toDate(),
      user_id: [] as Array<string | number>,
    },
    onSubmit: (values) => {},
  });

  const values = searchFormik.values;


  const graph = useMemo(() => {
    const base = vision === 'daily' ? getDatesBetween(values.from, values.to) : getMonthsBetween(values.from, values.to)

    const res = base.map(key => {
      const profit = (dashboard.data || []).reduce((prev: number, curr: {created_at: Date, profit: number}) => {
        const currKey = moment(curr.created_at).format(vision === 'daily' ? 'DD/MM/YYYY' : 'MM/YYYY')
        if (currKey === key) {
          return currency(prev).add(curr.profit).value
        }

        return prev
      }, 0)

      return {
        key,
        profit: profit || 0
      }
    })

    return res
  }, [dashboard.data, vision])

  useEffect(() => {
    refetchDashboard({
      params: {
        from: values.from,
        to: values.to,
        user_id: values.user_id,
      },
    });
  }, [values]);

  const [users] = useAxios("/user");

  const userOptions = useMemo(() => {
    return (
      users?.data
        ?.filter(
          ({ role }: { role: "ADMIN" | "SELLER" }) =>
            role === "ADMIN" || role === "SELLER"
        )
        .map((item: { name: string; id: number }) => ({
          value: item.id,
          label: item.name,
        })) || []
    );
  }, [users.data]);

  return (
    <Paper
      elevation={1}
      sx={{
        height: "100%",
        padding: 2,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          marginBottom: 10,
          alignItems: "center",
        }}
      >
        <Typography
          component="h2"
          variant="h6"
          color="primary"
          fontSize={20}
          flex={1}
        >
          RAM
        </Typography>
        <div
          style={{
            display: "flex",
            flex: 2,
            justifyContent: "space-between",
            gap: 1
          }}
        >
          <div style={{display: 'flex', flex: 1}}>
            <Input 
              options={[
                {label: 'Mensal', value: 'monthly'},
                {label: 'Diário', value: 'daily'},
              ]}
              value={vision}
              onChange={({target}) => {
                setVision(target.value as 'monthly')
              }}
              select
            />
          </div>
          <div style={{display: 'flex', flex: 1}}>
          <MultipleAutocomplete
            variant="outlined"
            label="Emissor"
            onChange={(value) => {
              searchFormik.setFieldValue(
                "user_id",
                (value as MultipleAutocompleteOption[])?.map(
                  (item) => item.value
                )
              );
            }}
            options={userOptions}
            value={userOptions.filter((item: MultipleAutocompleteOption) =>
              searchFormik.values.user_id.includes(item.value)
            )}
          />
          </div>
          <div style={{display: 'flex', flex: 2}}>
          <DateRangePicker
            startDate={searchFormik.values.from}
            endDate={searchFormik.values.to}
            onChange={(value) => {
              searchFormik.setFieldValue("from", value.startDate);
              searchFormik.setFieldValue("to", value.endDate);
            }}
          />
          </div>
        </div>
      </div>
      <div
        style={{
          height: "100%",
        }}
      >
        <ResponsiveContainer height={"90%"}>
          {!dashboard.loading ? (
            <LineChart
              data={graph || []}
              margin={{
                top: 16,
                right: 16,
                bottom: 0,
                left: 16,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="key"
                includeHidden
                stroke={theme.palette.text.secondary}
                style={theme.typography.body2}
                // tickFormatter={(tick) => {
                //   return capitalizeFirstLetter(
                //     moment(`${tick}/01`).format("MMM/YYYY")
                //   );
                // }}
                tickLine
                tickCount={12}
              ></XAxis>
              <YAxis
                stroke={theme.palette.text.secondary}
                style={theme.typography.body2}
                tickFormatter={(tick) => {
                  return formatMoney(tick);
                }}
                tick
              ></YAxis>
              <Line
                isAnimationActive={false}
                type="monotone"
                dataKey="profit"
                stroke={theme.palette.primary.main}
              />
              <Tooltip
                formatter={(value: number) => {
                  return [formatMoney(value)];
                }}
                // labelFormatter={(label) => {
                //   return capitalizeFirstLetter(
                //     moment(`${label}/01`).format("MMMM/YYYY")
                //   );
                // }}
                labelStyle={{
                  fontWeight: "bold",
                }}
              />
              <Legend
                formatter={() => {
                  return "RAM";
                }}
                fontWeight="bold"
              />
            </LineChart>
          ) : (
            <div />
          )}
        </ResponsiveContainer>
      </div>
    </Paper>
  );
};
